import React, { useCallback, useEffect, useState } from "react";
import { App, Card, Col, Row, Statistic, Typography } from "antd";
import { useThemeStyles } from "../../../context/ThemeContext";
import { getAdminMyGroupsBudgetPool } from "../../../services/api";

const { Text } = Typography;

const formatBudgetValue = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
};

export default function GroupBudgetPoolCards({ token, enabled = true }) {
  const themeStyles = useThemeStyles();
  const isLight = themeStyles.isLight;
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);

  const load = useCallback(async () => {
    if (!token || !enabled) return;
    setLoading(true);
    try {
      const res = await getAdminMyGroupsBudgetPool(token, 30);
      setGroups(Array.isArray(res?.data?.groups) ? res.data.groups : []);
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to load group budget pool");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [token, enabled, message]);

  useEffect(() => {
    load();
  }, [load]);

  if (!enabled) return null;

  const cardStyle = isLight
    ? {
        background: "#F3F4F6",
        border: "1px solid #D1D5DB",
        borderRadius: 10,
      }
    : {
        background: themeStyles.elevatedBg,
        border: `1px solid ${themeStyles.borderColor}`,
        borderRadius: 10,
      };

  if (!loading && groups.length === 0) {
    return (
      <Card style={{ marginBottom: 16, background: themeStyles.cardBg }} loading={loading}>
        <Text type="secondary">No groups assigned to your admin account.</Text>
      </Card>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {groups.map((group) => {
        const total = formatBudgetValue(group.group_budget_usd);
        const allocated = formatBudgetValue(group.total_allocated_to_projects_usd) ?? 0;
        const remaining = formatBudgetValue(group.remaining_unallocated_usd);
        const titlePrefix = groups.length > 1 ? `${group.group_name} — ` : "";

        return (
          <Row key={group.group_id} gutter={[16, 16]} style={{ marginBottom: groups.length > 1 ? 12 : 0 }}>
            <Col xs={24} sm={12} md={8}>
              <Card style={cardStyle} loading={loading}>
                <Statistic
                  title={`${titlePrefix}Group budget (total)`}
                  value={total == null ? "No cap" : total}
                  precision={total == null ? undefined : 2}
                  prefix={total == null ? undefined : "$"}
                  valueStyle={{ color: themeStyles.textPrimary }}
                />
                {total == null ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Super admin has not set a group spending cap.
                  </Text>
                ) : null}
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card style={cardStyle} loading={loading}>
                <Statistic
                  title={`${titlePrefix}Allocated to projects`}
                  value={allocated}
                  precision={4}
                  prefix="$"
                  valueStyle={{ color: themeStyles.textPrimary }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card style={cardStyle} loading={loading}>
                <Statistic
                  title={`${titlePrefix}Remaining to allocate`}
                  value={remaining == null ? "—" : remaining}
                  precision={remaining == null ? undefined : 4}
                  prefix={remaining == null ? undefined : "$"}
                  valueStyle={{ color: themeStyles.textPrimary }}
                />
              </Card>
            </Col>
          </Row>
        );
      })}
    </div>
  );
}
